import { useEffect } from "react";

import { usePlayer } from "../../stores/player";
import { useSession } from "../../stores/session";
import { useStudioSettings } from "../../stores/studio";
import { useRoomTuning } from "../../stores/room";
import { positionNow } from "./sync";
import type { Room } from "./api";

/**
 * Tunes the one audio element into a room, and keeps it fed.
 *
 * Renders nothing. Mounted once by `RoomFollower` in the shell rather than by
 * whatever page you happen to be on, so a broadcast survives navigation - and
 * so a host monitoring their own console hears exactly what their listeners
 * hear, which is the only way to notice that a track is silent or ends badly.
 *
 * ## Loading is a track change, not a position change
 *
 * The element is told to load only when the room moves to a *different* track.
 * Position is handled entirely by `useRoomSync`, which nudges the rate or seeks
 * without reloading anything. Reloading on every position update would restart
 * the audio four times a second.
 *
 * ## Joining mid-track starts in the right place
 *
 * `play()` starts at zero, so the first correction after the stream is
 * decodable does the seek. That is a fraction of a second of the wrong part of
 * a track on join, which is the cost of not having a way to tell the engine
 * where to begin before it has begun.
 */
/**
 * What the dock should say the room is playing, when it cannot read the player.
 *
 * Null in catalogue mode: the player is loaded there and is the better source,
 * because it knows what is *actually* decoding rather than what the room last
 * said.
 */
function streamingNowPlaying(
  room: Room,
): { title: string; artist: string } | null {
  if (room.source !== "stream" || !room.now_playing) return null;

  return {
    title: room.now_playing.title,
    artist: room.now_playing.artist.display_name,
  };
}

export function RoomAudio({ room, enabled }: { room: Room; enabled: boolean }) {
  /*
   * Whether the person listening is the person broadcasting.
   *
   * By username rather than by route: a host can be anywhere on the site while
   * their room is on air, and the deck has to keep playing wherever they are.
   */
  const isHost = useSession((state) => state.user?.username === room.username);
  /*
   * Whether *this tab* is the one running the studio.
   *
   * "Is the viewer the host" was the wrong question and doubled the audio: a
   * host who opened their own profile - in another tab, or in the same one now
   * that the studio survives navigation - got the deck playing locally *and*
   * the stream playing beside it, a second or so apart.
   *
   * The deck has to run in exactly one place: the tab whose `<audio>` element
   * the studio has tapped. Everywhere else, including the host's own profile,
   * the stream already carries it.
   */
  const producing = useStudioSettings((state) => state.open) && isHost;

  const tune = useRoomTuning((state) => state.tune);
  const update = useRoomTuning((state) => state.update);
  const retune = useRoomTuning((state) => state.retune);

  const trackId = room.now_playing?.id ?? null;

  /* ---------------------------------------------------------------------- */
  /* Follow the room's playhead                                             */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    if (!enabled) return;

    const playhead = {
      position_ms: room.position_ms,
      server_time: room.server_time,
      paused: room.paused,
      track_id: trackId,
    };

    // `tune` on first sight, `update` afterwards: `update` is a no-op when
    // nothing is tuned, which is what stops a stale render re-tuning somebody
    // who has left.
    if (useRoomTuning.getState().tuned?.id === room.id) {
      update(playhead);
      // `update` carries the playhead only, so in studio mode - where the dock
      // reads its title from here rather than from the player - a track change
      // would otherwise never reach it.
      retune(streamingNowPlaying(room));
    } else {
      tune(
        {
          id: room.id,
          username: room.username,
          name: room.name,
          host: room.host.display_name,
          studio: room.source === "stream",
          streaming: streamingNowPlaying(room),
        },
        playhead,
      );
    }
  }, [enabled, room, trackId, tune, update]);

  /* ---------------------------------------------------------------------- */
  /* Load when the track changes                                            */
  /* ---------------------------------------------------------------------- */
  useEffect(() => {
    if (!enabled || !room.now_playing) return;

    /*
     * In studio mode the dock stands down - for listeners, and only for them.
     *
     * A listener's copy of this track is already inside the host's stream, so
     * loading it here as well would mean hearing it twice, a second apart, with
     * no way to mute one of them.
     *
     * The tab running the studio is the opposite case, and getting this wrong
     * broke the feature: its deck is where the music *comes from*. The studio
     * mixes it by tapping this very element, so a host whose deck is not
     * playing broadcasts silence - and cannot monitor it either, because there
     * is nothing to hear.
     *
     * The room is still *tuned* for listeners, because being in the room is
     * what the listener count and the chat are keyed on. Only playback moves.
     */
    if (room.source === "stream" && !producing) return;

    const player = usePlayer.getState();

    if (player.track?.id === room.now_playing.id) return;

    const track = room.now_playing;

    void player.play(
      {
        id: track.id,
        title: track.title,
        duration_ms: track.duration_ms,
        waveform_url: track.waveform_url,
        artwork: track.artwork,
        artwork_small: track.artwork_small,
        owner: {
          username: track.artist.username,
          display_name: track.artist.display_name,
        },
      },
      // A single-item queue. The room decides what comes next, and a queue here
      // would give the dock a "next" that jumps out of the broadcast.
      undefined,
      "radio",
    );
    // `now_playing.id` rather than the object, which is a fresh reference on
    // every poll and would reload the stream every three seconds.
  }, [enabled, trackId, room.source, producing, room.now_playing?.id]);

  /* ---------------------------------------------------------------------- */
  /* Nothing to clean up on the way out                                     */
  /* ---------------------------------------------------------------------- */
  /*
   * This used to `leave()` when it unmounted, which is why listening ended the
   * moment you navigated away from the host's profile.
   *
   * It reads like tidy housekeeping and it is the opposite: unmounting is not a
   * decision anybody made. Following ends when somebody stops listening, when
   * they tune into a different broadcast, or when the host goes off air - all
   * three of which are explicit, and none of which is a component going away.
   * `stopListening` and `RoomFollower` own those.
   */

  return null;
}

/**
 * How far behind the room this listener is, in milliseconds.
 *
 * Exported for the small "in sync" readout - it is the one number that tells
 * somebody whether the thing they are hearing is the thing everybody else is
 * hearing, and it is worth showing rather than asking them to trust it.
 */
export function driftMs(): number | null {
  const { playhead } = useRoomTuning.getState();
  const { position } = usePlayer.getState();

  if (!playhead) return null;

  const target = positionNow(playhead);

  return target === null ? null : Math.round(target - position * 1000);
}
